import {
  NumericStepper,
  Box,
  Flex,
  Stack,
  Text,
  PrimaryButton,
} from "flicket-ui";
import { Badge, SlateRenderer } from "~components";
import { FixedRatioImage } from "~components/common/Image";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import PriceWithFee from "~components/reservation/common/common.PriceWithFee";
import { useRouter } from "next/router";
import noop from "lodash/noop";
import { DetailsModal } from "../DetailsModal/DetailsModal";
import { addQueryParams, removeQueryParams } from "~lib/helpers/queryParams";
import PriceOrFree from "../common/PriceOrFree/PriceOrFree";
import { useState } from "react";
import Modal from "~components/common/Modal";
import { hasRichTextContent } from "../../../../lib/helpers/hasRichTextContent";

interface INonSeatedListItem {
  ticket: IReleaseTicket;
  onChange: (value: number, ticketId: string, zoneId: string) => void;
  quantity: number;
  showHiddenFees: boolean;
  showHighDemandBadge: boolean;
  onClick?: () => void;
}

export const ExperienceSalesTicketTypeItem = ({
  ticket,
  onChange,
  quantity,
  showHiddenFees,
  showHighDemandBadge,
}: INonSeatedListItem) => {
  const router = useRouter();
  // Will be used to check if the ticket has the enhanced experience view
  const showEnhanced = ticket.description || ticket.imageGallery?.length > 0;

  function handleItemClick() {
    addQueryParams(router, { modalId: ticket.id });
  }

  function closeModal() {
    removeQueryParams(router, ["modalId"]);
  }

  return (
    <>
      <Modal
        alignModalTop={true}
        isOpen={router?.query?.modalId === ticket.id}
        close={closeModal}
        clickOutsideToClose={true}
      >
        <ModalContent
          ticketType={ticket}
          quantity={quantity}
          onChange={onChange}
        />
      </Modal>

      {!showEnhanced && (
        <CardItemBasic
          ticket={ticket}
          onChange={onChange}
          quantity={quantity}
          showHiddenFees={showHiddenFees}
          showHighDemandBadge={showHighDemandBadge}
          // onClick={handleItemClick}
        />
      )}

      {showEnhanced && (
        <CardItemEnhanced
          ticket={ticket}
          onClick={handleItemClick}
          onChange={onChange}
          quantity={quantity}
          showHiddenFees={showHiddenFees}
          showHighDemandBadge={showHighDemandBadge}
        />
      )}
    </>
  );
};

function Card({
  id,
  onClick,
  isSelected = false,
  children,
}: {
  id: string;
  onClick: () => void;
  isSelected: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      id={id}
      borderBottom="1px solid"
      borderColor={"N200"}
      onClick={onClick}
      cursor={onClick ? "pointer" : "auto"}
      css={`
        transition: box-shadow 0.15s;
        -webkit-tap-highlight-color: transparent;
        &:hover {
          /* background-color: ${(p) => p.theme.colors.N100}; */
        }
      `}
    >
      <Flex alignItems="center" py={2} flex={1}>
        {children}
      </Flex>
    </Box>
  );
}

function CardItemEnhanced({
  onChange,
  ticket,
  showHiddenFees = false,
  quantity = undefined,
  showHighDemandBadge,
  onClick = noop,
}: INonSeatedListItem) {
  return (
    <Card id={ticket.id} onClick={onClick} isSelected={quantity > 0}>
      <Stack
        gap={2}
        direction={["vertical", "vertical", "horizontal"]}
        justifyContent="space-between"
        flex={1}
      >
        {ticket.imageGallery?.[0] && (
          <Box>
            <FixedRatioImage
              src={ticket.imageGallery?.[0].original.src}
              alt={ticket.name}
              ratio={"196 / 130"}
              width={{ _: "100%", xs: 196 }}
              borderRadius="sm"
            />
          </Box>
        )}
        <Box flex={1}>
          <Stack gap={3} direction="horizontal" justifyContent="space-between">
            <Box flex={1}>
              <Text variant="header.S">{ticket.name}</Text>
              <TicketPriceAndFee ticket={ticket} showFees={showHiddenFees} />
            </Box>
            <Box onClick={(e) => e.stopPropagation()}>
              <NumericStepper
                value={quantity}
                onChange={(value: number) =>
                  onChange(value, ticket.id, ticket.zoneId)
                }
                min={ticket.minPurchaseQuantity}
                max={ticket.maxPurchaseQuantity}
              />
            </Box>
          </Stack>
          {showHighDemandBadge && <Badge>High demand</Badge>}
          {hasRichTextContent(ticket.description) && (
            <Box
              css={`
                -webkit-box-orient: vertical;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                overflow: hidden;
              `}
            >
              <SlateRenderer value={ticket.description} />
            </Box>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

function CardItemBasic({
  onChange,
  ticket,
  showHiddenFees = false,
  quantity = undefined,
  showHighDemandBadge,
  onClick,
}: INonSeatedListItem) {
  return (
    <Card id={ticket.id} onClick={onClick} isSelected={quantity > 0}>
      <Stack
        gap={3}
        direction="horizontal"
        justifyContent="space-between"
        flex={1}
      >
        <Box flex={1}>
          <Text variant="header.S">{ticket.name}</Text>
          <TicketPriceAndFee ticket={ticket} showFees={showHiddenFees} />
          {showHighDemandBadge && <Badge>In High demand</Badge>}
        </Box>
        <Box>
          <NumericStepper
            value={quantity}
            onChange={(value: number) =>
              onChange(value, ticket.id, ticket.zoneId)
            }
            min={ticket.minPurchaseQuantity}
            max={ticket.maxPurchaseQuantity}
          />
        </Box>
      </Stack>
    </Card>
  );
}

function ModalContent({
  ticketType,
  quantity,
  onChange,
}: {
  ticketType: IReleaseTicket;
  quantity: number;
  onChange: INonSeatedListItem["onChange"];
}) {
  const router = useRouter();
  const [tempQuantity, setTempQuantity] = useState(quantity);

  function closeModal() {
    removeQueryParams(router, ["modalId"]);
  }

  function onSubmit() {
    onChange(tempQuantity, ticketType.id, ticketType.zoneId);
    closeModal();
  }

  return (
    <DetailsModal
      isOpen={router?.query?.modalId === ticketType.id}
      close={closeModal}
      title={ticketType.name}
      description={ticketType.description}
      imageGallery={ticketType.imageGallery}
      stickySection={({ isSticky }) => (
        <Stack
          px={isSticky ? 4 : 0}
          gap={3}
          direction="horizontal"
          justifyContent="space-between"
          flex={1}
          alignItems="center"
        >
          <Box>
            <Text variant="header.M">{ticketType.name}</Text>
            <Text variant="regular">
              <PriceOrFree price={ticketType.price} />
            </Text>
          </Box>
          <Box display={{ _: "none", sm: "block" }}>
            <TicketTypeQunatitySelect
              ticketType={ticketType}
              quantity={tempQuantity}
              onChange={setTempQuantity}
              onSubmit={onSubmit}
            />
          </Box>
        </Stack>
      )}
      mobileFooter={
        <TicketTypeQunatitySelect
          ticketType={ticketType}
          quantity={tempQuantity}
          onChange={setTempQuantity}
          onSubmit={onSubmit}
        />
      }
    />
  );
}

function TicketTypeQunatitySelect({
  ticketType,
  quantity,
  onChange,
  onSubmit,
}: {
  ticketType: IReleaseTicket;
  quantity: number;
  onChange: (q: number) => void;
  onSubmit: () => void;
}) {
  console.log({ quantity });
  return (
    <>
      <Box>
        {quantity === undefined && (
          <PrimaryButton onClick={() => onChange(1)}>Select</PrimaryButton>
        )}
        {quantity !== undefined && (
          <Stack gap={2} alignItems="center">
            <NumericStepper
              value={quantity}
              onChange={(value: number) => onChange(value)}
              min={ticketType.minPurchaseQuantity}
              max={ticketType.maxPurchaseQuantity}
              allowZero={true}
            />
            <PrimaryButton w="150px" onClick={onSubmit}>
              {quantity > 0 ? "Add to order" : " Remove"}
            </PrimaryButton>
          </Stack>
        )}
      </Box>
    </>
  );
}

function TicketPriceAndFee({
  ticket,
  showFees,
}: {
  showFees: boolean;
  ticket: IReleaseTicket;
}) {
  return (
    <Box>
      <Text as="span" variant="regular">
        <PriceOrFree price={ticket.price} />
      </Text>
      {showFees && (
        <Text as="span" variant="small">
          {" "}
          <PriceWithFee.Fee showFees={true} ticket={ticket} />
        </Text>
      )}
    </Box>
  );
}
